export type Language = 'es' | 'en';

export type RouteContent = {
  placeName: string;
  welcomeTitle: string;
  welcomeDescription: string;
  languageQuestion: string;
  supportText: string;
  landingSurveyPrompt: string;
  durationLabel: string;
  savedLanguage: string;
  useSavedLanguage: string;
  pdfPath: string;
  audioPath: string;
  formatQuestion: string;
  readPdf: string;
  listenAudio: string;
  pdfDescription: string;
  audioDescription: string;
  surveyDescription: string;
  chooseFormat: string;
  backToFormats: string;
  audioTitle: string;
  audioDurationLabel: string;
  audioSupport: string;
  audioDownloadLabel: string;
  audioFinished: string;
  pdfOpenLabel: string;
  pdfDownloadLabel: string;
  tourTitle: string;
  backHome: string;
  changeLanguage: string;
  changeLanguageConfirm: string;
  previous: string;
  next: string;
  zoomIn: string;
  zoomOut: string;
  loadingPdf: string;
  pdfError: string;
  pageProgress: (page: number, total: number) => string;
  finalCardTitle: string;
  finalCardText: string;
  finalCardDismiss: string;
  finalTitle: string;
  finalText: string;
  finalQuestion: string;
  surveyCta: string;
  surveySmallText: string;
  privacyNote: string;
  notFoundTitle: string;
  notFoundText: string;
};

export const languages: Record<Language, string> = {
  es: 'Español',
  en: 'English',
};

export const content: Record<Language, RouteContent> = {
  es: {
    placeName: 'Albania Restaurante',
    welcomeTitle: 'Bienvenido a Albania Restaurante',
    welcomeDescription:
      'Este recorrido guiado te permitirá conocer la historia, las piezas, los sabores y los espacios que hacen parte de nuestra experiencia.',
    languageQuestion: '¿En qué idioma prefieres continuar?',
    supportText: 'No necesitas descargar ninguna aplicación.',
    landingSurveyPrompt: '¿Ya terminaste el recorrido? Comparte tu experiencia.',
    durationLabel: 'Recorrido estimado: 20 minutos',
    savedLanguage: 'Último idioma usado',
    useSavedLanguage: 'Continuar en español',
    pdfPath: '/pdf/recorrido-es.pdf',
    audioPath: '/audio/recorrido-es.mp3',
    formatQuestion: '¿Cómo prefieres hacer el recorrido?',
    readPdf: 'Abrir PDF',
    listenAudio: 'Escuchar audio',
    pdfDescription: 'Abre el recorrido completo con desplazamiento continuo y máxima nitidez.',
    audioDescription: 'Escucha la narración completa mientras recorres los espacios.',
    surveyDescription: 'Abre la encuesta de satisfacción en Google Forms.',
    chooseFormat: 'Formato del recorrido',
    backToFormats: 'Volver a las opciones',
    audioTitle: 'Recorrido narrado',
    audioDurationLabel: 'Audio: 22 minutos',
    audioSupport: 'Usa audífonos para disfrutar la narración sin interrumpir a otros visitantes.',
    audioDownloadLabel: 'Descargar audio',
    audioFinished: 'Has terminado el recorrido narrado.',
    pdfOpenLabel: 'Abrir PDF',
    pdfDownloadLabel: 'Descargar PDF',
    tourTitle: 'Recorrido guiado',
    backHome: 'Inicio',
    changeLanguage: 'Cambiar idioma',
    changeLanguageConfirm: 'Cambiar el idioma reiniciará el recorrido. ¿Deseas continuar?',
    previous: 'Anterior',
    next: 'Siguiente',
    zoomIn: 'Aumentar zoom',
    zoomOut: 'Reducir zoom',
    loadingPdf: 'Cargando recorrido...',
    pdfError: 'No pudimos cargar el visor. Puedes abrir el PDF directamente.',
    pageProgress: (page, total) => `Página ${page} de ${total}`,
    finalCardTitle: 'Llegaste al final del recorrido',
    finalCardText: 'Gracias por caminar esta historia con nosotros. Tu opinión nos ayuda a mejorar.',
    finalCardDismiss: 'Minimizar',
    finalTitle: 'Gracias por hacer este recorrido',
    finalText: 'Tu opinión nos ayuda a mejorar la experiencia para quienes nos visitan.',
    finalQuestion: '¿Cómo fue tu experiencia?',
    surveyCta: 'Evaluar el recorrido',
    surveySmallText: 'La encuesta toma menos de un minuto.',
    privacyNote: 'Al continuar, abrirás un formulario externo administrado con Google Forms.',
    notFoundTitle: 'Ruta no encontrada',
    notFoundText: 'Puedes volver al inicio para escoger el idioma del recorrido.',
  },
  en: {
    placeName: 'Albania Restaurante',
    welcomeTitle: 'Welcome to Albania Restaurante',
    welcomeDescription:
      'This guided tour will introduce you to the history, pieces, flavors and spaces that shape our experience.',
    languageQuestion: 'Which language would you like to use?',
    supportText: 'No app download is required.',
    landingSurveyPrompt: 'Have you finished the tour? Share your experience.',
    durationLabel: 'Estimated tour: 20 minutes',
    savedLanguage: 'Last language used',
    useSavedLanguage: 'Continue in English',
    pdfPath: '/pdf/tour-en.pdf',
    audioPath: '/audio/tour-en.mp3',
    formatQuestion: 'How would you like to take the tour?',
    readPdf: 'Open PDF',
    listenAudio: 'Listen to audio',
    pdfDescription: 'Open the complete tour with continuous scrolling and full clarity.',
    audioDescription: 'Listen to the full narration as you explore the spaces.',
    surveyDescription: 'Open the satisfaction survey in Google Forms.',
    chooseFormat: 'Tour format',
    backToFormats: 'Back to tour options',
    audioTitle: 'Narrated tour',
    audioDurationLabel: 'Audio: 25 minutes',
    audioSupport: 'Use headphones to enjoy the narration without disturbing other visitors.',
    audioDownloadLabel: 'Download audio',
    audioFinished: 'You have completed the narrated tour.',
    pdfOpenLabel: 'Open PDF',
    pdfDownloadLabel: 'Download PDF',
    tourTitle: 'Guided tour',
    backHome: 'Home',
    changeLanguage: 'Change language',
    changeLanguageConfirm: 'Changing language will restart the tour. Do you want to continue?',
    previous: 'Previous',
    next: 'Next',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    loadingPdf: 'Loading tour...',
    pdfError: 'We could not load the viewer. You can open the PDF directly.',
    pageProgress: (page, total) => `Page ${page} of ${total}`,
    finalCardTitle: 'You reached the end of the tour',
    finalCardText: 'Thank you for walking through this story with us. Your feedback helps us improve.',
    finalCardDismiss: 'Minimize',
    finalTitle: 'Thank you for taking the tour',
    finalText: 'Your feedback helps us improve the experience for every visitor.',
    finalQuestion: 'How was your experience?',
    surveyCta: 'Rate the tour',
    surveySmallText: 'The survey takes less than one minute.',
    privacyNote: 'By continuing, you will open an external form managed with Google Forms.',
    notFoundTitle: 'Page not found',
    notFoundText: 'You can return home to choose the tour language.',
  },
};

export const tourRoutes: Record<Language, string> = {
  es: '/es/recorrido',
  en: '/en/tour',
};

export const thanksRoutes: Record<Language, string> = {
  es: '/es/gracias',
  en: '/en/thank-you',
};
